import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ComponentType } from 'react';
import {
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
} from 'react-icons/fi';

import { API_BASE_PATH } from '../apiConfig';
import {
  compactScopePaths,
  requestAdvancedScan,
  requestFreeGitHubScan,
  requestFreeGitHubScanAllowance,
  requestJson,
  waitForMinimumScanLaunchLoading,
  type LaunchState,
  type LoadState,
  type PricingEstimateResponse,
  type PricingPlan,
  type PricingPlanEstimate,
  type ScanAcceptedResponse,
  type ScanAllowanceResponse
} from '../components/workflowShared';
import type { PaidScanPaymentSubmission } from '../components/PaidScanPaymentModal';
import {
  RUN_GITHUB_PROTECTED_SCREENS,
  RUN_NAV_ITEMS,
  RunNavIcon,
  RunNewWorkspaceScreen,
  RunPricingScreen,
  RunScopeScreen,
  RunSettingsScreen,
  RunToolsScreen,
  type AccountUser,
  type GitHubLoadState,
  type RunPageScreen,
  type Workspace,
  type WorkspaceScope,
  type WorkspaceScanSummary
} from '../components/screens/RunScreens';
import RunGitHubAccount, { RunGitHubLoginModal } from './RunGitHubAccount';
import RunIssueDashboard from './RunIssueDashboard';
import RunStatusPage from './RunStatusPage';

type RunIconComponent = ComponentType<{ 'aria-hidden'?: boolean }>;

const IconChevronDown = FiChevronDown as unknown as RunIconComponent;
const IconChevronLeft = FiChevronLeft as unknown as RunIconComponent;
const IconChevronRight = FiChevronRight as unknown as RunIconComponent;
const IconDownload = FiDownload as unknown as RunIconComponent;

type RunPageLoadState = 'loading' | 'refreshing' | 'ready' | 'error';

interface RunPageProps {
  runId: string | null;
}

interface RunStatusModel {
  model: string;
  aiAgent: string;
  status: string;
  started: boolean;
  finished: boolean;
  startedAt?: string;
  finishedAt?: string;
  command?: string;
  exitCode?: number;
  timedOut?: boolean;
  issueCount?: number;
  submissionErrorCount?: number;
  error?: string;
}

interface RunLogEntry {
  time: string;
  message: string;
}

interface RunStatusAgent {
  agent: string;
  status: string;
  started: boolean;
  finished: boolean;
  startedAt?: string;
  finishedAt?: string;
  logFilePath?: string;
  logs?: RunLogEntry[];
  models: RunStatusModel[];
  error?: string;
}

interface RunRequestedAiAgentModel {
  aiAgent: string;
  model?: string;
}

interface RunStatusDeduplication {
  status: string;
  finished: boolean;
  model?: string;
  judgeModel?: string;
  startedAt?: string;
  finishedAt?: string;
  outputPath?: string;
  issueCount?: number;
  deduplicatedIssueCount?: number;
  reportedIssueCount?: number;
  openAiRequestCount?: number;
  error?: string;
}

interface RunStatusSnapshot {
  runId: string;
  scanTier?: 'free' | 'advanced';
  target: string;
  requestedSkills: string[];
  requestedAiAgents: string[];
  requestedAiAgentModels?: RunRequestedAiAgentModel[];
  runDirectory: string;
  statusFilePath: string;
  branch?: string;
  scopePath?: string;
  scopePaths?: string[];
  preconditionsPath?: string;
  clonedRepositoryPath?: string;
  repositoryPath?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  agents: RunStatusAgent[];
  deduplication?: RunStatusDeduplication;
  report?: RunStatusDeduplication;
  setupError?: string;
}

interface RunIssueSubmission {
  Title: string;
  Summary: string;
  Detail: string;
  Impact: string;
  Files: string[];
  severity?: string;
}

interface RunRawIssue {
  issueId: number;
  skill: string;
  scanAgent?: string;
  aiAgent?: string;
  relativePath: string;
  issue: RunIssueSubmission;
}

interface RunReportedIssue extends RunIssueSubmission {
  id: string;
  sourceIssueIds: number[];
  sources: RunRawIssue[];
  duplicateReasons: string[];
}

interface RunIssueReadError {
  relativePath: string;
  error: string;
}

type RunIssueAcceptanceStatus = 'acknowledged' | 'accepted';

interface RunAcceptedIssue {
  id: string;
  status: RunIssueAcceptanceStatus;
  updatedAt: string;
}

interface RunIssuesPayload {
  allIssues?: RunRawIssue[];
  duplicatedIssues?: RunRawIssue[];
  deduplicatedIssues?: RunReportedIssue[];
  reportedIssues?: RunReportedIssue[];
  acceptedIssues?: RunAcceptedIssue[];
  readErrors?: RunIssueReadError[];
  deduplicationGeneratedAt?: string;
  reportGeneratedAt?: string;
  acceptedIssuesUpdatedAt?: string;
}

interface RunDetailsResponse {
  status: RunStatusSnapshot;
  issues: RunIssuesPayload;
}

interface RunIssueViewModel {
  id: string;
  title: string;
  summary: string;
  detail: string;
  impact: string;
  files: string[];
  sources: string[];
  severity?: string;
  acceptanceStatus?: RunIssueAcceptanceStatus;
  acceptanceUpdatedAt?: string;
}

interface RunAcceptedIssueUpdateResponse {
  acceptedIssues: RunAcceptedIssue[];
  acceptedIssuesUpdatedAt?: string;
}

interface GitHubAppConfigResponse {
  configured: boolean;
  appSlug?: string;
  installUrl?: string;
  connectUrl?: string;
  missing?: string[];
}

interface GitHubSessionResponse {
  sessionId: string;
  login?: string;
}

interface GitHubInstallationSummary {
  id: number;
  account: {
    login: string;
    type?: string;
    avatarUrl?: string;
  };
  targetType?: string;
  repositorySelection?: string;
  htmlUrl?: string;
  appSlug?: string;
}

interface GitHubRepositorySummary {
  id: number;
  owner: string;
  repo: string;
  fullName: string;
  private: boolean;
  htmlUrl?: string;
  defaultBranch?: string;
  permissions?: Record<string, boolean>;
}

interface GitHubRepositoryChoice {
  installation: GitHubInstallationSummary;
  repository: GitHubRepositorySummary;
}

interface GitHubInstallationsResponse {
  installations?: GitHubInstallationSummary[];
}

interface GitHubRepositoriesResponse {
  repositories?: GitHubRepositorySummary[];
}

interface GitHubRepositoryBranchSummary {
  name: string;
  default: boolean;
  protected?: boolean;
  commitSha?: string;
}

interface GitHubBranchesResponse {
  repository?: {
    fullName?: string;
    defaultBranch?: string;
  };
  branches?: GitHubRepositoryBranchSummary[];
}

interface GitHubRepositoryDirectorySummary {
  path: string;
  name: string;
  depth: number;
}

interface GitHubRepositoryTreeResponse {
  repository?: {
    fullName?: string;
    defaultBranch?: string;
    branch?: string;
  };
  directories?: GitHubRepositoryDirectorySummary[];
}

type GitHubRepositoriesByInstallation = Record<number, GitHubRepositorySummary[]>;

const RUN_POLL_INTERVAL_MS = 3000;
const RUN_TERMINAL_STATUSES = ['completed', 'partial', 'failed'];
const RUN_REPORT_TERMINAL_STATUSES = ['completed', 'failed', 'skipped'];
const GITHUB_SESSION_STORAGE_KEY = 'scas-github-session-id';
const GITHUB_INSTALLATION_STORAGE_KEY = 'scas-github-installation-id';
const LEGACY_GITHUB_SCOPE_STORAGE_KEY = 'scas-github-scope-draft';
const WORKSPACE_STORAGE_KEY = 'scas-current-workspace-id';
const DEV_GITHUB_SESSION_ID = 'dev-github-session';
const DEV_GITHUB_INSTALLATION_ID = 99101;
const DEV_GITHUB_REPOSITORY_ID = 424201;
const DEV_PLATFORM_FEE_USD = 10;
const DEV_LIGHT_PLATFORM_FEE_USD = 1;
const DEV_DAYBREAK_SHORT_CONTEXT_INPUT_RATE_USD_PER_MILLION = 4;
const DEV_DAYBREAK_LONG_CONTEXT_INPUT_RATE_USD_PER_MILLION = 8;
const DEV_CLAUDE_OPUS_INPUT_RATE_USD_PER_MILLION = 5;
const DEV_LONG_CONTEXT_INPUT_TOKEN_THRESHOLD = 272000;

const runIsDevGitHubAccountEnabled = (): boolean => {
  return process.env.NODE_ENV === 'development' && Boolean(process.env.REACT_APP_DEV_GITHUB_ACCOUNT?.trim());
};

const runGetDevGitHubLogin = (): string => {
  const configuredLogin = process.env.REACT_APP_DEV_GITHUB_ACCOUNT?.trim() ?? '';

  return configuredLogin && !['1', 'true', 'yes'].includes(configuredLogin.toLowerCase())
    ? configuredLogin
    : 'havu-dev';
};

const runGetDevGitHubInstallations = (): GitHubInstallationSummary[] => {
  const login = runGetDevGitHubLogin();

  return [
    {
      id: DEV_GITHUB_INSTALLATION_ID,
      account: {
        login,
        type: 'User'
      },
      targetType: 'User',
      repositorySelection: 'selected',
      htmlUrl: 'https://github.com/settings/installations/dev',
      appSlug: 'havu-dev'
    }
  ];
};

const runGetDevGitHubRepositories = (): GitHubRepositorySummary[] => {
  const login = runGetDevGitHubLogin();

  return [
    {
      id: DEV_GITHUB_REPOSITORY_ID,
      owner: login,
      repo: 'marketplace-api',
      fullName: `${login}/marketplace-api`,
      private: true,
      htmlUrl: `https://github.com/${login}/marketplace-api`,
      defaultBranch: 'main',
      permissions: {
        admin: true,
        maintain: true,
        push: true,
        pull: true
      }
    },
    {
      id: DEV_GITHUB_REPOSITORY_ID + 1,
      owner: login,
      repo: 'payments-contracts',
      fullName: `${login}/payments-contracts`,
      private: true,
      htmlUrl: `https://github.com/${login}/payments-contracts`,
      defaultBranch: 'audit-readiness',
      permissions: {
        admin: true,
        maintain: true,
        push: true,
        pull: true
      }
    }
  ];
};

const DEV_GITHUB_BRANCHES: GitHubRepositoryBranchSummary[] = [
  {
    name: 'main',
    default: true,
    protected: true,
    commitSha: '8f4a27c'
  },
  {
    name: 'audit-readiness',
    default: false,
    protected: false,
    commitSha: 'd26bc91'
  },
  {
    name: 'feature/checkout-hardening',
    default: false,
    protected: false,
    commitSha: '51a9e20'
  }
];

const DEV_GITHUB_TREE_DIRECTORIES: GitHubRepositoryDirectorySummary[] = [
  { path: 'api', name: 'api', depth: 0 },
  { path: 'api/controllers', name: 'controllers', depth: 1 },
  { path: 'api/middleware', name: 'middleware', depth: 1 },
  { path: 'api/routes', name: 'routes', depth: 1 },
  { path: 'api/services', name: 'services', depth: 1 },
  { path: 'api/services/billing', name: 'billing', depth: 2 },
  { path: 'api/services/github', name: 'github', depth: 2 },
  { path: 'contracts', name: 'contracts', depth: 0 },
  { path: 'contracts/src', name: 'src', depth: 1 },
  { path: 'contracts/test', name: 'test', depth: 1 },
  { path: 'frontend', name: 'frontend', depth: 0 },
  { path: 'frontend/src', name: 'src', depth: 1 },
  { path: 'frontend/src/components', name: 'components', depth: 2 },
  { path: 'frontend/src/pages', name: 'pages', depth: 2 },
  { path: 'infra', name: 'infra', depth: 0 },
  { path: 'docs', name: 'docs', depth: 0 }
];

const DEV_ACCOUNT_USER_ID = 'dev-user-id';
const DEV_WORKSPACE_ID = 'dev-workspace-id';
const DEV_WORKSPACE_ID_SECONDARY = 'dev-workspace-id-secondary';

const runGetDevAccountUser = (): AccountUser => {
  const login = runGetDevGitHubLogin();

  return {
    id: DEV_ACCOUNT_USER_ID,
    githubLogin: login,
    githubAvatarUrl: `https://avatars.githubusercontent.com/${login}`,
    email: `${login}@example.com`
  };
};

const runGetDevWorkspaces = (): Workspace[] => {
  const now = new Date().toISOString();

  return [
    {
      id: DEV_WORKSPACE_ID,
      ownerUserId: DEV_ACCOUNT_USER_ID,
      name: 'Acme Protocol',
      slug: 'acme-protocol',
      createdAt: now,
      updatedAt: now
    },
    {
      id: DEV_WORKSPACE_ID_SECONDARY,
      ownerUserId: DEV_ACCOUNT_USER_ID,
      name: 'Sandbox',
      slug: 'sandbox',
      createdAt: now,
      updatedAt: now
    }
  ];
};

const runGetDevWorkspaceScans = (workspaceId: string): WorkspaceScanSummary[] => {
  if (workspaceId !== DEV_WORKSPACE_ID) {
    return [];
  }

  const hoursAgo = (hours: number): string => new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();

  // Ordered newest-first, matching the backend's `ORDER BY created_at DESC`. Targets are
  // labeled "Run N: <hash>" so the mock data reads unambiguously as a run history in the UI.
  return [
    {
      id: 'dev-scan-running',
      workspaceId,
      createdByUserId: DEV_ACCOUNT_USER_ID,
      scanType: 'web-api',
      target: 'Run 3: f02e1',
      status: 'running',
      sourceType: 'github',
      branch: 'feature/checkout-hardening',
      createdAt: hoursAgo(0.25),
      updatedAt: hoursAgo(0.1)
    },
    {
      id: 'dev-scan-partial',
      workspaceId,
      createdByUserId: DEV_ACCOUNT_USER_ID,
      scanType: 'smart-contract',
      target: 'Run 2: 88b1d',
      status: 'partial',
      sourceType: 'github',
      branch: 'audit-readiness',
      createdAt: hoursAgo(4),
      updatedAt: hoursAgo(3),
      completedAt: hoursAgo(3)
    },
    {
      id: 'dev-scan-completed',
      workspaceId,
      createdByUserId: DEV_ACCOUNT_USER_ID,
      scanType: 'smart-contract',
      target: 'Run 1: a3f9c',
      status: 'completed',
      sourceType: 'github',
      branch: 'main',
      createdAt: hoursAgo(26),
      updatedAt: hoursAgo(25),
      completedAt: hoursAgo(25)
    }
  ];
};

const DEV_FULL_SKILLS = [
  'semgrep-code-security',
  'trail-of-bits-static-analysis',
  'trail-of-bits-supply-chain',
  'quillshield',
  'forefy'
];
const DEV_GLM_MODEL = { aiAgent: 'opencode', model: 'openrouter/z-ai/glm-5.3', provider: 'zai' as const, rate: 0.6 };
const DEV_TERRA_MODEL = { aiAgent: 'codex', model: 'gpt-5.6-terra', provider: 'openai' as const, rate: 2 };

const runGetDevPricingEstimate = (scopePaths: string[]): PricingEstimateResponse => {
  const scopeMultiplier = scopePaths.length === 0 ? 1 : Math.max(0.28, Math.min(0.72, scopePaths.length * 0.18));
  const estimatedCodebaseTokens = Math.round(930000 * scopeMultiplier);
  const averageInputTokensPerRun = Math.round(540000 * scopeMultiplier);
  const daybreakRate = averageInputTokensPerRun > DEV_LONG_CONTEXT_INPUT_TOKEN_THRESHOLD
    ? DEV_DAYBREAK_LONG_CONTEXT_INPUT_RATE_USD_PER_MILLION
    : DEV_DAYBREAK_SHORT_CONTEXT_INPUT_RATE_USD_PER_MILLION;
  const fullModels = [
    { aiAgent: 'codex', model: 'gpt-daybreak-blue-latest', provider: 'openai' as const, rate: daybreakRate },
    { aiAgent: 'claude', model: 'claude-opus-5', provider: 'anthropic' as const, rate: DEV_CLAUDE_OPUS_INPUT_RATE_USD_PER_MILLION }
  ];
  const buildPlan = (
    runs: { skill: string; model: typeof DEV_GLM_MODEL | typeof fullModels[number] }[],
    platformFeeUsd: number,
    isFree = false
  ): PricingPlanEstimate => {
    const models = Array.from(new Set(runs.map((run) => run.model)));
    const providerModelCosts = models.map((model) => {
      const scanRunCount = runs.filter((run) => run.model === model).length;
      const estimatedInputTokens = scanRunCount * averageInputTokensPerRun;
      const inputTokenRateUsdPerMillion = isFree ? 0 : model.rate;

      return {
        aiAgent: model.aiAgent,
        model: model.model,
        provider: model.provider,
        scanRunCount,
        estimatedInputTokens,
        inputTokenRateUsdPerMillion,
        apiCostUsd: Number((estimatedInputTokens / 1000000 * inputTokenRateUsdPerMillion).toFixed(2))
      };
    });
    const apiCostUsd = Number(providerModelCosts.reduce((total, entry) => total + entry.apiCostUsd, 0).toFixed(2));
    const selectedSkills = Array.from(new Set(runs.map((run) => run.skill)));

    return {
      apiCostUsd,
      platformFeeUsd,
      totalUsd: Number((apiCostUsd + platformFeeUsd).toFixed(2)),
      selectedSkillCount: selectedSkills.length,
      selectedModelCount: models.length,
      scanRunCount: runs.length,
      selectedSkills,
      tokenEstimate: {
        baselineFullScanInputTokens: 5400000,
        baselineSkillRunCount: 10,
        baselineCodebaseTokens: 930000,
        estimatedCodebaseTokens,
        codebaseSizeMultiplier: scopeMultiplier,
        averageInputTokensPerRun,
        totalEstimatedInputTokens: providerModelCosts.reduce((total, entry) => total + entry.estimatedInputTokens, 0)
      },
      providerModelCosts,
      selectedAiAgentModels: models.map((model) => ({ aiAgent: model.aiAgent, model: model.model }))
    };
  };
  const crossProduct = (models: (typeof DEV_GLM_MODEL | typeof fullModels[number])[]) => {
    return DEV_FULL_SKILLS.flatMap((skill) => models.map((model) => ({ skill, model })));
  };
  const plans = {
    full: buildPlan(crossProduct(fullModels), DEV_PLATFORM_FEE_USD),
    light: buildPlan(crossProduct([DEV_GLM_MODEL, DEV_TERRA_MODEL]), DEV_LIGHT_PLATFORM_FEE_USD),
    free: buildPlan([
      { skill: 'trail-of-bits-supply-chain', model: DEV_GLM_MODEL },
      { skill: 'semgrep-code-security', model: DEV_GLM_MODEL },
      { skill: 'trail-of-bits-static-analysis', model: DEV_GLM_MODEL },
      { skill: 'trail-of-bits-static-analysis', model: DEV_TERRA_MODEL }
    ], 0, true)
  };

  return {
    ...plans.full,
    pricingType: 'web-api',
    currency: 'USD',
    codebase: {
      scopePaths,
      fileCount: Math.round(812 * scopeMultiplier),
      countedFileCount: Math.round(436 * scopeMultiplier),
      totalBytes: Math.round(5200000 * scopeMultiplier),
      nonCommentLineCount: Math.round(78500 * scopeMultiplier)
    },
    plans
  };
};

const runFormatErrorMessage = (payload: unknown, fallbackStatus: number): string => {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const { error } = payload as { error?: unknown };

    if (typeof error === 'string' && error.trim()) {
      return error;
    }
  }

  return `Request failed with status ${fallbackStatus}`;
};

const runRequestJson = async <ResponseType,>(path: string, init: RequestInit = {}): Promise<ResponseType> => {
  const headers = new Headers(init.headers);

  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_BASE_PATH}${path}`, {
    ...init,
    headers,
    credentials: 'include'
  });
  const payload = await response.json().catch(() => undefined) as unknown;

  if (!response.ok) {
    throw new Error(runFormatErrorMessage(payload, response.status));
  }

  return payload as ResponseType;
};

const runGetIssueMarkdownExportUrl = (runId: string): string => {
  return `${API_BASE_PATH}/runs/${encodeURIComponent(runId)}/issues.md`;
};

const runGetIssuePdfExportUrl = (runId: string): string => {
  return `${API_BASE_PATH}/runs/${encodeURIComponent(runId)}/issues.pdf`;
};

const runGetResultProcessingStatus = (status: RunStatusSnapshot | undefined): RunStatusDeduplication | undefined => {
  return status?.deduplication ?? status?.report;
};

const runGetStoredGitHubSessionId = (): string => {
  return window.localStorage.getItem(GITHUB_SESSION_STORAGE_KEY) ?? '';
};

const runStoreGitHubSessionId = (sessionId: string): void => {
  if (sessionId === DEV_GITHUB_SESSION_ID) {
    return;
  }

  if (sessionId) {
    window.localStorage.setItem(GITHUB_SESSION_STORAGE_KEY, sessionId);
  } else {
    window.localStorage.removeItem(GITHUB_SESSION_STORAGE_KEY);
  }
};

const runGetInitialGitHubSessionId = (): string => {
  if (runIsDevGitHubAccountEnabled()) {
    return DEV_GITHUB_SESSION_ID;
  }

  const params = new URLSearchParams(window.location.search);

  return params.get('github_session') ?? runGetStoredGitHubSessionId();
};

const runGetGitHubCallbackError = (): string => {
  if (runIsDevGitHubAccountEnabled()) {
    return '';
  }

  return new URLSearchParams(window.location.search).get('github_error') ?? '';
};

const runGetGitHubSetupInstallationId = (): number | null => {
  if (runIsDevGitHubAccountEnabled()) {
    return DEV_GITHUB_INSTALLATION_ID;
  }

  const params = new URLSearchParams(window.location.search);
  const installationId = Number(
    params.get('installation_id') ?? window.localStorage.getItem(GITHUB_INSTALLATION_STORAGE_KEY)
  );

  return Number.isSafeInteger(installationId) && installationId > 0 ? installationId : null;
};

const runStoreGitHubInstallationId = (installationId: number | null): void => {
  if (runIsDevGitHubAccountEnabled()) {
    return;
  }

  if (installationId) {
    window.localStorage.setItem(GITHUB_INSTALLATION_STORAGE_KEY, String(installationId));
  } else {
    window.localStorage.removeItem(GITHUB_INSTALLATION_STORAGE_KEY);
  }
};

const runClearStoredAccountState = (): void => {
  window.localStorage.removeItem(GITHUB_SESSION_STORAGE_KEY);
  window.localStorage.removeItem(GITHUB_INSTALLATION_STORAGE_KEY);
  window.localStorage.removeItem(WORKSPACE_STORAGE_KEY);
};

const runGetStoredWorkspaceId = (): string => {
  return window.localStorage.getItem(WORKSPACE_STORAGE_KEY) ?? '';
};

const runStoreWorkspaceId = (workspaceId: string): void => {
  if (workspaceId) {
    window.localStorage.setItem(WORKSPACE_STORAGE_KEY, workspaceId);
  } else {
    window.localStorage.removeItem(WORKSPACE_STORAGE_KEY);
  }
};

const runClearGitHubCallbackParams = (): void => {
  const params = new URLSearchParams(window.location.search);
  const callbackParams = [
    'github_session',
    'github_connected',
    'github_error',
    'installation_id',
    'setup_action',
    'state'
  ];
  const hasCallbackParam = callbackParams.some((param) => params.has(param));

  if (!hasCallbackParam) {
    return;
  }

  for (const param of callbackParams) {
    params.delete(param);
  }

  const nextSearch = params.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;

  window.history.replaceState({}, document.title, nextUrl);
};

const runIsReady = (details: RunDetailsResponse | null): boolean => {
  if (!details) {
    return false;
  }

  const resultProcessing = runGetResultProcessingStatus(details.status);

  return RUN_TERMINAL_STATUSES.includes(details.status.status)
    && Boolean(resultProcessing)
    && RUN_REPORT_TERMINAL_STATUSES.includes(resultProcessing?.status ?? '');
};

const runGetIssues = (details: RunDetailsResponse | null): RunIssueViewModel[] => {
  if (!details?.issues) {
    return [];
  }

  const acceptedById = new Map(
    (details.issues.acceptedIssues ?? []).map((issue) => [issue.id, issue])
  );
  const reportedIssues = details.issues.deduplicatedIssues ?? details.issues.reportedIssues ?? [];

  if (reportedIssues.length > 0) {
    return reportedIssues.map((issue) => {
      const acceptedIssue = acceptedById.get(issue.id);

      return {
        id: issue.id,
        title: issue.Title,
        summary: issue.Summary,
        detail: issue.Detail,
        impact: issue.Impact,
        files: issue.Files,
        severity: issue.severity,
        acceptanceStatus: acceptedIssue?.status,
        acceptanceUpdatedAt: acceptedIssue?.updatedAt,
        sources: issue.sources.map((source) => {
          const scanAgent = source.scanAgent ? ` / ${source.scanAgent}` : '';
          const aiAgent = source.aiAgent ? ` / ${source.aiAgent}` : '';

          return `${source.skill}${scanAgent}${aiAgent} #${source.issueId}`;
        })
      };
    });
  }

  return (details.issues.allIssues ?? []).map((entry) => {
    const id = String(entry.issueId);
    const acceptedIssue = acceptedById.get(id);

    return {
      id,
      title: entry.issue.Title,
      summary: entry.issue.Summary,
      detail: entry.issue.Detail,
      impact: entry.issue.Impact,
      files: entry.issue.Files,
      severity: entry.issue.severity,
      acceptanceStatus: acceptedIssue?.status,
      acceptanceUpdatedAt: acceptedIssue?.updatedAt,
      sources: [`${entry.skill}${entry.scanAgent ? ` / ${entry.scanAgent}` : ''}${entry.aiAgent ? ` / ${entry.aiAgent}` : ''}`]
    };
  });
};

function RunPage({ runId }: RunPageProps) {
  const initialGithubSessionId = useMemo(() => runGetInitialGitHubSessionId(), []);
  const [loadState, setLoadState] = useState<RunPageLoadState>(runId ? 'loading' : 'ready');
  const [details, setDetails] = useState<RunDetailsResponse | null>(null);
  const [error, setError] = useState('');
  const [screen, setScreen] = useState<RunPageScreen>(runId ? 'findings' : 'scope');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [githubConfigState, setGithubConfigState] = useState<GitHubLoadState>('idle');
  const [githubConfigError, setGithubConfigError] = useState('');
  const [githubConfig, setGithubConfig] = useState<GitHubAppConfigResponse | null>(null);
  const [githubSessionId, setGithubSessionId] = useState(initialGithubSessionId);
  const [githubSessionState, setGithubSessionState] = useState<GitHubLoadState>('idle');
  const [githubSessionError, setGithubSessionError] = useState(runGetGitHubCallbackError());
  const [githubSessionLogin, setGithubSessionLogin] = useState('');
  const [isGitHubLoginPromptOpen, setIsGitHubLoginPromptOpen] = useState(!runId && !initialGithubSessionId);
  const [githubInstallations, setGithubInstallations] = useState<GitHubInstallationSummary[]>([]);
  const [selectedGithubInstallationId, setSelectedGithubInstallationId] = useState<number | null>(
    () => runGetGitHubSetupInstallationId()
  );
  const [githubRepositoriesState, setGithubRepositoriesState] = useState<GitHubLoadState>('idle');
  const [githubRepositoriesError, setGithubRepositoriesError] = useState('');
  const [githubRepositoriesByInstallation, setGithubRepositoriesByInstallation] =
    useState<GitHubRepositoriesByInstallation>({});
  const [selectedGithubRepositoryId, setSelectedGithubRepositoryId] = useState<number | null>(
    null
  );
  const [githubBranchesState, setGithubBranchesState] = useState<GitHubLoadState>('idle');
  const [githubBranchesError, setGithubBranchesError] = useState('');
  const [githubBranches, setGithubBranches] = useState<GitHubRepositoryBranchSummary[]>([]);
  const [selectedGithubBranch, setSelectedGithubBranch] = useState('');
  const [githubTreeState, setGithubTreeState] = useState<GitHubLoadState>('idle');
  const [githubTreeError, setGithubTreeError] = useState('');
  const [githubTreeDirectories, setGithubTreeDirectories] = useState<GitHubRepositoryDirectorySummary[]>([]);
  const [scopePaths, setScopePaths] = useState<string[]>([]);
  const [assumptionsFilePath, setAssumptionsFilePath] = useState('');
  const [workspaceScopeSaveState, setWorkspaceScopeSaveState] = useState<LaunchState>('idle');
  const [workspaceScopeSaveError, setWorkspaceScopeSaveError] = useState('');
  const [pricingEstimateState, setPricingEstimateState] = useState<LoadState>('idle');
  const [pricingEstimateError, setPricingEstimateError] = useState('');
  const [pricingEstimate, setPricingEstimate] = useState<PricingEstimateResponse | null>(null);
  const [launchState, setLaunchState] = useState<LaunchState>('idle');
  const [launchError, setLaunchError] = useState('');
  const [scanResponse, setScanResponse] = useState<ScanAcceptedResponse | null>(null);
  const [freeScanAllowance, setFreeScanAllowance] = useState<ScanAllowanceResponse | null>(null);
  const [isResultEmailPromptOpen, setIsResultEmailPromptOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [selectedPricingPlan, setSelectedPricingPlan] = useState<PricingPlan>('full');
  const [resultEmail, setResultEmail] = useState('');
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [, setAccountState] = useState<GitHubLoadState>('idle');
  const [accountUser, setAccountUser] = useState<AccountUser | null>(null);
  const [workspacesState, setWorkspacesState] = useState<LoadState>('idle');
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string>(() => runGetStoredWorkspaceId());
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [createWorkspaceState, setCreateWorkspaceState] = useState<LaunchState>('idle');
  const [createWorkspaceError, setCreateWorkspaceError] = useState('');
  const [workspaceScansState, setWorkspaceScansState] = useState<LoadState>('idle');
  const [workspaceScans, setWorkspaceScans] = useState<WorkspaceScanSummary[]>([]);
  const [logoutState, setLogoutState] = useState<LaunchState>('idle');
  const [logoutError, setLogoutError] = useState('');

  const isReady = runIsReady(details);
  const isGitHubAuthenticated = Boolean(githubSessionId && githubSessionState === 'ready');
  const isAccountAuthenticated = Boolean(accountUser);
  const currentWorkspace = workspaces.find((workspace) => workspace.id === currentWorkspaceId);
  const issues = runGetIssues(details);
  const selectedGithubInstallation = githubInstallations.find((installation) => {
    return installation.id === selectedGithubInstallationId;
  });
  const githubRepositoryChoices = useMemo<GitHubRepositoryChoice[]>(() => {
    return githubInstallations.flatMap((installation) => {
      const repositories = githubRepositoriesByInstallation[installation.id] ?? [];

      return repositories.map((repository) => ({
        installation,
        repository
      }));
    });
  }, [githubInstallations, githubRepositoriesByInstallation]);
  const selectedGithubRepository = (selectedGithubInstallationId
    ? githubRepositoriesByInstallation[selectedGithubInstallationId] ?? []
    : []
  ).find((repository) => {
    return repository.id === selectedGithubRepositoryId;
  });
  const canRunGitHubScan = Boolean(
    githubSessionId &&
    selectedGithubInstallationId &&
    selectedGithubRepository &&
    (selectedGithubBranch || selectedGithubRepository?.defaultBranch)
  );
  const hasSelectedScope = scopePaths.length > 0;
  const headerRunButtonLabel = !isGitHubAuthenticated && !hasSelectedScope ? 'Run Deep Pentest' : 'Run Pentest';
  const selectedRepositoryLabel = selectedGithubRepository
    ? `${selectedGithubRepository.fullName}${selectedGithubBranch ? `:${selectedGithubBranch}` : ''}`
    : '';

  useEffect(() => {
    // Scope is workspace data now. Remove drafts written by older frontend versions.
    window.localStorage.removeItem(LEGACY_GITHUB_SCOPE_STORAGE_KEY);
  }, []);

  const buildGitHubScanPayload = useCallback((email?: string): Record<string, unknown> | null => {
    if (!githubSessionId || !selectedGithubInstallationId || !selectedGithubRepository) {
      return null;
    }

    const branch = selectedGithubBranch || selectedGithubRepository.defaultBranch;

    if (!branch) {
      return null;
    }

    return {
      github: {
        app: {
          sessionId: githubSessionId,
          installationId: selectedGithubInstallationId,
          repositoryId: selectedGithubRepository.id,
          owner: selectedGithubRepository.owner,
          repo: selectedGithubRepository.repo,
          branch
        }
      },
      scopePaths,
      ...(email ? { resultEmail: email } : {}),
      ...(currentWorkspaceId ? { workspaceId: currentWorkspaceId } : {}),
      questionnaire: {
        assumptionsFilePath
      }
    };
  }, [
    assumptionsFilePath,
    currentWorkspaceId,
    githubSessionId,
    scopePaths,
    selectedGithubBranch,
    selectedGithubInstallationId,
    selectedGithubRepository
  ]);

  const loadRun = useCallback(async () => {
    if (!runId) {
      return;
    }

    setLoadState((previous) => previous === 'ready' ? 'refreshing' : previous === 'error' ? 'loading' : previous);
    setError('');

    try {
      const response = await runRequestJson<RunDetailsResponse>(`/getResults/${encodeURIComponent(runId)}`);

      setDetails(response);
      setLoadState('ready');
    } catch (loadError) {
      console.error('Failed to load run data:', loadError);
      setError('Unable to load this run. Please try again.');
      setLoadState('error');
    }
  }, [runId]);

  const loadPricingEstimate = useCallback(async () => {
    const payload = buildGitHubScanPayload();

    if (!payload) {
      setPricingEstimate(null);
      setPricingEstimateState('idle');
      setPricingEstimateError('');
      return;
    }

    if (githubSessionId === DEV_GITHUB_SESSION_ID && runIsDevGitHubAccountEnabled()) {
      setPricingEstimate(runGetDevPricingEstimate(scopePaths));
      setPricingEstimateState('ready');
      setPricingEstimateError('');
      return;
    }

    setPricingEstimateState('loading');
    setPricingEstimateError('');

    try {
      const estimate = await requestJson<PricingEstimateResponse>('/pricing', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      setPricingEstimate(estimate);
      setPricingEstimateState('ready');
    } catch (pricingError) {
      console.error('Failed to estimate pricing:', pricingError);
      setPricingEstimate(null);
      setPricingEstimateError('Unable to estimate pricing for this repository.');
      setPricingEstimateState('error');
    }
  }, [buildGitHubScanPayload, githubSessionId, scopePaths]);

  // The free scan allowance is per signed-in user and repository, so only the repository matters here.
  const loadFreeScanAllowance = useCallback(async () => {
    const github = buildGitHubScanPayload()?.github;

    if (!github || githubSessionId === DEV_GITHUB_SESSION_ID) {
      setFreeScanAllowance(null);
      return;
    }

    try {
      setFreeScanAllowance(await requestFreeGitHubScanAllowance({ github }));
    } catch (allowanceError) {
      // Unknown allowance: leave the button enabled and let the backend decide when the scan starts.
      console.error('Failed to load the free scan allowance:', allowanceError);
      setFreeScanAllowance(null);
    }
  }, [buildGitHubScanPayload, githubSessionId]);

  const loadAccount = useCallback(async () => {
    if (runIsDevGitHubAccountEnabled()) {
      setAccountUser(runGetDevAccountUser());
      setAccountState('ready');
      return;
    }

    setAccountState('loading');

    try {
      const user = await runRequestJson<AccountUser>('/auth/me');

      setAccountUser(user);
      setAccountState('ready');
    } catch {
      setAccountUser(null);
      setAccountState('ready');
    }
  }, []);

  const loadWorkspaces = useCallback(async () => {
    if (runIsDevGitHubAccountEnabled()) {
      const devWorkspaces = runGetDevWorkspaces();

      setWorkspaces(devWorkspaces);
      setWorkspacesState('ready');
      setCurrentWorkspaceId((currentId) => {
        return currentId && devWorkspaces.some((workspace) => workspace.id === currentId)
          ? currentId
          : devWorkspaces[0]?.id ?? '';
      });
      return;
    }

    setWorkspacesState('loading');

    try {
      const response = await runRequestJson<{ workspaces?: Workspace[] }>('/workspaces');
      const loadedWorkspaces = response.workspaces ?? [];

      setWorkspaces(loadedWorkspaces);
      setWorkspacesState('ready');
      setCurrentWorkspaceId((currentId) => {
        if (currentId && loadedWorkspaces.some((workspace) => workspace.id === currentId)) {
          return currentId;
        }

        return loadedWorkspaces[0]?.id ?? '';
      });
    } catch (workspacesError) {
      console.error('Failed to load workspaces:', workspacesError);
      setWorkspaces([]);
      setWorkspacesState('error');
    }
  }, []);

  const loadWorkspaceScans = useCallback(async (workspaceId: string) => {
    if (!workspaceId) {
      setWorkspaceScans([]);
      setWorkspaceScansState('idle');
      return;
    }

    if (runIsDevGitHubAccountEnabled()) {
      setWorkspaceScans(runGetDevWorkspaceScans(workspaceId));
      setWorkspaceScansState('ready');
      return;
    }

    setWorkspaceScansState('loading');

    try {
      const response = await runRequestJson<{ scans?: WorkspaceScanSummary[] }>(
        `/workspaces/${encodeURIComponent(workspaceId)}/scans`
      );

      setWorkspaceScans(response.scans ?? []);
      setWorkspaceScansState('ready');
    } catch (scansError) {
      console.error('Failed to load workspace runs:', scansError);
      setWorkspaceScans([]);
      setWorkspaceScansState('error');
    }
  }, []);

  const createWorkspace = useCallback(async () => {
    const name = newWorkspaceName.trim();

    if (!name) {
      return;
    }

    if (runIsDevGitHubAccountEnabled()) {
      const now = new Date().toISOString();
      const workspace: Workspace = {
        id: `dev-workspace-${Date.now()}`,
        ownerUserId: DEV_ACCOUNT_USER_ID,
        name,
        slug: name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'workspace',
        createdAt: now,
        updatedAt: now
      };

      setWorkspaces((current) => [workspace, ...current]);
      setCurrentWorkspaceId(workspace.id);
      setNewWorkspaceName('');
      setCreateWorkspaceState('idle');
      return;
    }

    setCreateWorkspaceState('submitting');
    setCreateWorkspaceError('');

    try {
      const response = await runRequestJson<{ workspace: Workspace }>('/workspaces', {
        method: 'POST',
        body: JSON.stringify({ name })
      });

      setWorkspaces((current) => [response.workspace, ...current]);
      setCurrentWorkspaceId(response.workspace.id);
      setNewWorkspaceName('');
      setCreateWorkspaceState('idle');
    } catch (createError) {
      console.error('Failed to create workspace:', createError);
      setCreateWorkspaceState('error');
      setCreateWorkspaceError('Unable to create the workspace. Please try again.');
    }
  }, [newWorkspaceName]);

  const selectWorkspace = useCallback((workspaceId: string) => {
    setCurrentWorkspaceId(workspaceId);
  }, []);

  const loadGitHubConfig = useCallback(async () => {
    if (githubSessionId === DEV_GITHUB_SESSION_ID && runIsDevGitHubAccountEnabled()) {
      setGithubConfig({
        configured: true,
        appSlug: 'havu-dev',
        installUrl: 'https://github.com/settings/installations/dev',
        connectUrl: 'https://github.com/login/oauth/authorize'
      });
      setGithubConfigState('ready');
      setGithubConfigError('');
      return;
    }

    setGithubConfigState('loading');
    setGithubConfigError('');

    try {
      const config = await runRequestJson<GitHubAppConfigResponse>('/github/app');

      setGithubConfig(config);
      setGithubConfigState('ready');
    } catch (configError) {
      console.error('Failed to load GitHub App config:', configError);
      setGithubConfig(null);
      setGithubConfigError('Unable to load GitHub App configuration.');
      setGithubConfigState('error');
    }
  }, [githubSessionId]);

  const loadGitHubSession = useCallback(async (sessionId: string) => {
    if (!sessionId) {
      setGithubInstallations([]);
      setGithubRepositoriesByInstallation({});
      setGithubSessionLogin('');
      setGithubSessionState('idle');
      return;
    }

    if (sessionId === DEV_GITHUB_SESSION_ID && runIsDevGitHubAccountEnabled()) {
      const installations = runGetDevGitHubInstallations();

      setGithubSessionLogin(runGetDevGitHubLogin());
      setGithubInstallations(installations);
      setGithubSessionState('ready');
      setGithubSessionError('');
      setSelectedGithubInstallationId(DEV_GITHUB_INSTALLATION_ID);
      return;
    }

    setGithubSessionState('loading');
    setGithubSessionError('');

    try {
      const [sessionResponse, installationResponse] = await Promise.all([
        runRequestJson<GitHubSessionResponse>(`/github/session/${encodeURIComponent(sessionId)}`),
        runRequestJson<GitHubInstallationsResponse>(
          `/github/session/${encodeURIComponent(sessionId)}/installations`
        )
      ]);
      const installations = Array.isArray(installationResponse.installations)
        ? installationResponse.installations
        : [];

      setGithubSessionLogin(sessionResponse.login ?? '');
      setGithubInstallations(installations);
      setGithubSessionState('ready');
      setSelectedGithubInstallationId((currentInstallationId) => {
        if (currentInstallationId && installations.some((installation) => installation.id === currentInstallationId)) {
          return currentInstallationId;
        }

        return installations[0]?.id ?? null;
      });
    } catch (sessionError) {
      console.error('Failed to load GitHub session:', sessionError);
      setGithubInstallations([]);
      setGithubRepositoriesByInstallation({});
      setGithubSessionLogin('');
      setGithubSessionState('error');
      setGithubSessionError('Unable to load GitHub installations.');
      runStoreGitHubSessionId('');
    }
  }, []);

  const loadGitHubRepositories = useCallback(async (
    sessionId: string,
    installations: GitHubInstallationSummary[]
  ) => {
    if (!sessionId || installations.length === 0) {
      setGithubRepositoriesByInstallation({});
      setGithubRepositoriesState(sessionId ? 'ready' : 'idle');
      setGithubRepositoriesError('');
      return;
    }

    if (sessionId === DEV_GITHUB_SESSION_ID && runIsDevGitHubAccountEnabled()) {
      setGithubRepositoriesByInstallation({
        [DEV_GITHUB_INSTALLATION_ID]: runGetDevGitHubRepositories()
      });
      setGithubRepositoriesState('ready');
      setGithubRepositoriesError('');
      return;
    }

    setGithubRepositoriesState('loading');
    setGithubRepositoriesError('');

    try {
      const entries = await Promise.all(installations.map(async (installation) => {
        const response = await runRequestJson<GitHubRepositoriesResponse>(
          `/github/session/${encodeURIComponent(sessionId)}/installations/${String(installation.id)}/repositories`
        );
        const repositories = Array.isArray(response.repositories) ? response.repositories : [];

        return [installation.id, repositories] as const;
      }));
      const repositoriesByInstallation = entries.reduce<GitHubRepositoriesByInstallation>(
        (groupedRepositories, [installationId, repositories]) => ({
          ...groupedRepositories,
          [installationId]: repositories
        }),
        {}
      );

      setGithubRepositoriesByInstallation(repositoriesByInstallation);
      setGithubRepositoriesState('ready');
    } catch (repositoriesError) {
      console.error('Failed to load GitHub repositories:', repositoriesError);
      setGithubRepositoriesByInstallation({});
      setSelectedGithubRepositoryId(null);
      setGithubRepositoriesState('error');
      setGithubRepositoriesError('Unable to load GitHub repositories.');
    }
  }, []);

  const loadGitHubBranches = useCallback(async (
    sessionId: string,
    installationId: number,
    repositoryId: number
  ) => {
    if (!sessionId || !installationId || !repositoryId) {
      setGithubBranches([]);
      setGithubBranchesState('idle');
      setGithubBranchesError('');
      setSelectedGithubBranch('');
      return;
    }

    if (sessionId === DEV_GITHUB_SESSION_ID && runIsDevGitHubAccountEnabled()) {
      const branches = repositoryId === DEV_GITHUB_REPOSITORY_ID
        ? DEV_GITHUB_BRANCHES
        : DEV_GITHUB_BRANCHES.map((branch) => ({
          ...branch,
          default: branch.name === 'audit-readiness'
        }));
      const fallbackBranch = branches.find((branch) => branch.default)?.name ?? branches[0]?.name ?? '';

      setGithubBranches(branches);
      setSelectedGithubBranch((currentBranch) => {
        return branches.some((branch) => branch.name === currentBranch)
          ? currentBranch
          : fallbackBranch;
      });
      setGithubBranchesState('ready');
      setGithubBranchesError('');
      return;
    }

    setGithubBranchesState('loading');
    setGithubBranchesError('');

    try {
      const response = await runRequestJson<GitHubBranchesResponse>(
        `/github/session/${encodeURIComponent(sessionId)}/installations/${String(installationId)}/repositories/${String(repositoryId)}/branches`
      );
      const branches = Array.isArray(response.branches) ? response.branches : [];
      const fallbackBranch = branches.find((branch) => branch.default)?.name ?? branches[0]?.name ?? '';

      setGithubBranches(branches);
      setSelectedGithubBranch((currentBranch) => {
        return branches.some((branch) => branch.name === currentBranch)
          ? currentBranch
          : fallbackBranch;
      });
      setGithubBranchesState('ready');
    } catch (branchesError) {
      console.error('Failed to load GitHub branches:', branchesError);
      setGithubBranches([]);
      setSelectedGithubBranch('');
      setGithubBranchesState('error');
      setGithubBranchesError('Unable to load repository branches.');
    }
  }, []);

  const loadGitHubRepositoryTree = useCallback(async (
    sessionId: string,
    installationId: number,
    repositoryId: number,
    branch: string,
    signal: AbortSignal
  ) => {
    if (!sessionId || !installationId || !repositoryId || !branch) {
      setGithubTreeDirectories([]);
      setGithubTreeState('idle');
      setGithubTreeError('');
      return;
    }

    if (sessionId === DEV_GITHUB_SESSION_ID && runIsDevGitHubAccountEnabled()) {
      const directories = repositoryId === DEV_GITHUB_REPOSITORY_ID
        ? DEV_GITHUB_TREE_DIRECTORIES
        : DEV_GITHUB_TREE_DIRECTORIES.filter((directory) => directory.path !== 'frontend/src/pages');

      setGithubTreeDirectories(directories);
      setScopePaths((currentScopePaths) => {
        const availablePaths = new Set(directories.map((directory) => directory.path));

        return compactScopePaths(currentScopePaths.filter((path) => availablePaths.has(path)));
      });
      setGithubTreeState('ready');
      setGithubTreeError('');
      return;
    }

    setGithubTreeState('loading');
    setGithubTreeError('');

    try {
      const response = await runRequestJson<GitHubRepositoryTreeResponse>(
        `/github/session/${encodeURIComponent(sessionId)}/installations/${String(installationId)}/repositories/${String(repositoryId)}/tree?branch=${encodeURIComponent(branch)}`,
        { signal }
      );
      const directories = Array.isArray(response.directories) ? response.directories : [];

      setGithubTreeDirectories(directories);
      setScopePaths((currentScopePaths) => {
        const availablePaths = new Set(directories.map((directory) => directory.path));

        return compactScopePaths(currentScopePaths.filter((path) => availablePaths.has(path)));
      });
      setGithubTreeState('ready');
    } catch (treeError) {
      if (treeError instanceof DOMException && treeError.name === 'AbortError') {
        return;
      }

      console.error('Failed to load GitHub tree:', treeError);
      setGithubTreeDirectories([]);
      setGithubTreeState('error');
      setGithubTreeError('Unable to load repository folders.');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    if (!runId) {
      return undefined;
    }

    const poll = async () => {
      setLoadState((previous) => previous === 'ready' ? 'refreshing' : previous === 'error' ? 'loading' : previous);
      setError('');

      try {
        const response = await runRequestJson<RunDetailsResponse>(`/getResults/${encodeURIComponent(runId)}`);

        if (cancelled) {
          return;
        }

        setDetails(response);
        setLoadState('ready');

        if (!runIsReady(response)) {
          timer = window.setTimeout(poll, RUN_POLL_INTERVAL_MS);
        }
      } catch (pollError) {
        if (cancelled) {
          return;
        }

        console.error('Failed to refresh run data:', pollError);
        setError('Unable to load this run. Please try again.');
        setLoadState('error');
      }
    };

    void poll();

    return () => {
      cancelled = true;

      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [runId]);

  useEffect(() => {
    void loadGitHubConfig();
  }, [loadGitHubConfig]);

  useEffect(() => {
    if (githubSessionId) {
      runStoreGitHubSessionId(githubSessionId);
      void loadGitHubSession(githubSessionId);
    } else {
      setGithubInstallations([]);
      setGithubRepositoriesByInstallation({});
      setGithubSessionLogin('');
      setSelectedGithubInstallationId(null);
      setSelectedGithubRepositoryId(null);
      setGithubBranches([]);
      setGithubBranchesState('idle');
      setGithubBranchesError('');
      setSelectedGithubBranch('');
      setGithubTreeDirectories([]);
      setGithubTreeState('idle');
      setGithubTreeError('');
      setScopePaths([]);
    }

    runClearGitHubCallbackParams();
  }, [githubSessionId, loadGitHubSession]);

  useEffect(() => {
    if (!githubSessionId) {
      setGithubRepositoriesByInstallation({});
      setSelectedGithubRepositoryId(null);
      setGithubBranches([]);
      setGithubBranchesState('idle');
      setGithubBranchesError('');
      setSelectedGithubBranch('');
      setGithubRepositoriesState('idle');
      setGithubTreeDirectories([]);
      setGithubTreeState('idle');
      setGithubTreeError('');
      setScopePaths([]);
      return;
    }

    if (githubSessionState === 'ready') {
      void loadGitHubRepositories(githubSessionId, githubInstallations);
    } else {
      setGithubRepositoriesByInstallation({});
      setGithubRepositoriesState(githubSessionState === 'loading' ? 'loading' : 'idle');
    }
  }, [githubSessionId, githubSessionState, githubInstallations, loadGitHubRepositories]);

  useEffect(() => {
    if (!githubSessionId || githubRepositoriesState !== 'ready') {
      return;
    }

    if (githubRepositoryChoices.length === 0) {
      setSelectedGithubRepositoryId(null);
      setSelectedGithubBranch('');
      setScopePaths([]);
      return;
    }

    const selectedChoice = githubRepositoryChoices.find((choice) => {
      return choice.installation.id === selectedGithubInstallationId &&
        choice.repository.id === selectedGithubRepositoryId;
    });

    if (selectedChoice) {
      return;
    }

    const matchingRepositoryChoice = selectedGithubRepositoryId
      ? githubRepositoryChoices.find((choice) => choice.repository.id === selectedGithubRepositoryId)
      : null;
    const nextChoice = matchingRepositoryChoice ?? (
      githubRepositoryChoices.length === 1 ? githubRepositoryChoices[0] : null
    );

    if (nextChoice) {
      setSelectedGithubInstallationId(nextChoice.installation.id);
      setSelectedGithubRepositoryId(nextChoice.repository.id);
      return;
    }

    setSelectedGithubRepositoryId(null);
    setSelectedGithubBranch('');
    setScopePaths([]);
  }, [
    githubRepositoryChoices,
    githubRepositoriesState,
    githubSessionId,
    selectedGithubInstallationId,
    selectedGithubRepositoryId
  ]);

  useEffect(() => {
    runStoreGitHubInstallationId(selectedGithubInstallationId);
  }, [selectedGithubInstallationId]);

  useEffect(() => {
    if (!githubSessionId || !selectedGithubInstallationId || !selectedGithubRepository) {
      if (githubSessionId && selectedGithubInstallationId && selectedGithubRepositoryId && githubRepositoriesState !== 'ready') {
        setGithubBranchesState('loading');
        setGithubTreeState('loading');
        return;
      }

      setGithubBranches([]);
      setGithubBranchesState(githubSessionId && selectedGithubRepositoryId ? 'loading' : 'idle');
      setGithubBranchesError('');
      setSelectedGithubBranch('');
      setGithubTreeDirectories([]);
      setGithubTreeState('idle');
      setGithubTreeError('');
      setScopePaths([]);
      return;
    }

    void loadGitHubBranches(githubSessionId, selectedGithubInstallationId, selectedGithubRepository.id);
  }, [
    githubSessionId,
    selectedGithubInstallationId,
    selectedGithubRepository,
    selectedGithubRepository?.id,
    selectedGithubRepositoryId,
    githubRepositoriesState,
    loadGitHubBranches
  ]);

  useEffect(() => {
    if (!githubSessionId || !selectedGithubInstallationId || !selectedGithubRepository || !selectedGithubBranch) {
      setGithubTreeDirectories([]);
      setGithubTreeState(selectedGithubRepositoryId ? 'loading' : 'idle');
      setGithubTreeError('');
      return;
    }

    const abortController = new AbortController();

    void loadGitHubRepositoryTree(
      githubSessionId,
      selectedGithubInstallationId,
      selectedGithubRepository.id,
      selectedGithubBranch,
      abortController.signal
    );

    return () => abortController.abort();
  }, [
    githubSessionId,
    selectedGithubBranch,
    selectedGithubInstallationId,
    selectedGithubRepository,
    selectedGithubRepository?.id,
    selectedGithubRepositoryId,
    loadGitHubRepositoryTree
  ]);

  useEffect(() => {
    setScanResponse(null);
    setIsPaymentOpen(false);
    setLaunchState((currentLaunchState) => currentLaunchState === 'accepted' ? 'idle' : currentLaunchState);
  }, [buildGitHubScanPayload]);

  useEffect(() => {
    if (screen === 'pricing' && canRunGitHubScan) {
      void loadPricingEstimate();
    }
  }, [canRunGitHubScan, loadPricingEstimate, screen]);

  useEffect(() => {
    if (screen === 'pricing' && canRunGitHubScan && isAccountAuthenticated) {
      void loadFreeScanAllowance();
    }
  }, [canRunGitHubScan, isAccountAuthenticated, loadFreeScanAllowance, screen]);

  useEffect(() => {
    setNowMs(Date.now());

    if (isReady) {
      return undefined;
    }

    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);

    return () => window.clearInterval(timer);
  }, [isReady]);

  useEffect(() => {
    void loadAccount();
  }, [loadAccount]);

  useEffect(() => {
    if (isAccountAuthenticated) {
      void loadWorkspaces();
    } else {
      setWorkspaces([]);
      setWorkspacesState('idle');
    }
  }, [isAccountAuthenticated, loadWorkspaces]);

  useEffect(() => {
    runStoreWorkspaceId(currentWorkspaceId);
  }, [currentWorkspaceId]);

  useEffect(() => {
    void loadWorkspaceScans(currentWorkspaceId);
  }, [currentWorkspaceId, loadWorkspaceScans]);

  useEffect(() => {
    if (workspacesState !== 'ready') {
      return;
    }

    const workspaceScope = currentWorkspace?.scope;

    setSelectedGithubInstallationId(workspaceScope?.installationId ?? null);
    setSelectedGithubRepositoryId(workspaceScope?.repositoryId ?? null);
    setSelectedGithubBranch(workspaceScope?.branch ?? '');
    setScopePaths(compactScopePaths(workspaceScope?.scopePaths ?? []));
    setAssumptionsFilePath(workspaceScope?.assumptionsFilePath ?? '');
    setWorkspaceScopeSaveState('idle');
    setWorkspaceScopeSaveError('');
  }, [currentWorkspace, workspacesState]);

  const handleIssueAcceptanceChange = useCallback(async (
    issueId: string,
    acceptanceStatus: RunIssueAcceptanceStatus | null
  ) => {
    if (!runId) {
      return;
    }

    const response = await runRequestJson<RunAcceptedIssueUpdateResponse>(
      `/runs/${encodeURIComponent(runId)}/issues/${encodeURIComponent(issueId)}/acceptance`,
      {
        method: 'POST',
        body: JSON.stringify({ status: acceptanceStatus ?? 'open' })
      }
    );

    setDetails((currentDetails) => currentDetails
      ? {
        ...currentDetails,
        issues: {
          ...currentDetails.issues,
          acceptedIssues: response.acceptedIssues,
          acceptedIssuesUpdatedAt: response.acceptedIssuesUpdatedAt
        }
      }
      : currentDetails);
  }, [runId]);

  const openGitHubLoginPrompt = useCallback(() => {
    setIsGitHubLoginPromptOpen(true);
  }, []);

  const closeGitHubLoginPrompt = useCallback(() => {
    setIsGitHubLoginPromptOpen(false);
  }, []);

  const reviewPricing = useCallback(async () => {
    if (
      !currentWorkspaceId ||
      !selectedGithubInstallationId ||
      !selectedGithubRepositoryId ||
      !selectedGithubBranch
    ) {
      setScreen('pricing');
      return;
    }

    const scope: WorkspaceScope = {
      installationId: selectedGithubInstallationId,
      repositoryId: selectedGithubRepositoryId,
      branch: selectedGithubBranch,
      scopePaths: compactScopePaths(scopePaths),
      ...(assumptionsFilePath.trim() ? { assumptionsFilePath: assumptionsFilePath.trim() } : {})
    };

    setWorkspaceScopeSaveState('submitting');
    setWorkspaceScopeSaveError('');

    try {
      if (runIsDevGitHubAccountEnabled()) {
        setWorkspaces((current) => current.map((workspace) => {
          return workspace.id === currentWorkspaceId ? { ...workspace, scope } : workspace;
        }));
      } else {
        const response = await runRequestJson<{ workspace: Workspace }>(
          `/workspaces/${encodeURIComponent(currentWorkspaceId)}`,
          {
            method: 'PATCH',
            body: JSON.stringify({ scope })
          }
        );

        setWorkspaces((current) => current.map((workspace) => {
          return workspace.id === response.workspace.id ? response.workspace : workspace;
        }));
      }

      setWorkspaceScopeSaveState('accepted');
      setScreen('pricing');
    } catch (scopeSaveError) {
      console.error('Failed to save workspace scope:', scopeSaveError);
      setWorkspaceScopeSaveState('error');
      setWorkspaceScopeSaveError('Unable to save this workspace scope. Please try again.');
    }
  }, [
    assumptionsFilePath,
    currentWorkspaceId,
    scopePaths,
    selectedGithubBranch,
    selectedGithubInstallationId,
    selectedGithubRepositoryId
  ]);

  const handleNavItemClick = useCallback((nextScreen: RunPageScreen) => {
    if (RUN_GITHUB_PROTECTED_SCREENS.has(nextScreen) && !isGitHubAuthenticated) {
      openGitHubLoginPrompt();
      return;
    }

    if (nextScreen === 'pricing') {
      void reviewPricing();
      return;
    }

    setScreen(nextScreen);
  }, [isGitHubAuthenticated, openGitHubLoginPrompt, reviewPricing]);

  const connectGitHub = useCallback(() => {
    const connectUrl = githubConfig?.connectUrl;

    if (!connectUrl) {
      setGithubSessionError('GitHub App OAuth is not configured.');
      return;
    }

    const returnTo = `${window.location.pathname}${window.location.search}`;

    window.location.href = `${connectUrl}?returnTo=${encodeURIComponent(returnTo)}`;
  }, [githubConfig?.connectUrl]);

  const logout = useCallback(async () => {
    setLogoutState('submitting');
    setLogoutError('');

    try {
      if (!runIsDevGitHubAccountEnabled()) {
        await runRequestJson<undefined>('/auth/logout', { method: 'POST' });
      }

      runClearStoredAccountState();
      setGithubSessionId('');
      setGithubSessionError('');
      setGithubSessionLogin('');
      setGithubSessionState('idle');
      setGithubInstallations([]);
      setGithubRepositoriesByInstallation({});
      setSelectedGithubInstallationId(null);
      setSelectedGithubRepositoryId(null);
      setGithubRepositoriesState('idle');
      setGithubBranches([]);
      setGithubBranchesState('idle');
      setGithubBranchesError('');
      setSelectedGithubBranch('');
      setGithubTreeDirectories([]);
      setGithubTreeState('idle');
      setGithubTreeError('');
      setScopePaths([]);
      setAssumptionsFilePath('');
      setPricingEstimate(null);
      setPricingEstimateState('idle');
      setFreeScanAllowance(null);
      setAccountUser(null);
      setAccountState('ready');
      setWorkspaces([]);
      setWorkspacesState('idle');
      setCurrentWorkspaceId('');
      setWorkspaceScans([]);
      setWorkspaceScansState('idle');
      setLogoutState('idle');
    } catch (logoutErrorValue) {
      console.error('Failed to log out:', logoutErrorValue);
      setLogoutState('error');
      setLogoutError('Unable to log out. Please try again.');
    }
  }, []);

  useEffect(() => {
    if (isGitHubAuthenticated) {
      setIsGitHubLoginPromptOpen(false);
    }
  }, [isGitHubAuthenticated]);

  const viewRun = useCallback((nextRunId: string) => {
    window.location.href = `/run/${encodeURIComponent(nextRunId)}`;
  }, []);

  const closeResultEmailPrompt = useCallback(() => {
    if (launchState !== 'submitting') {
      setIsResultEmailPromptOpen(false);
    }
  }, [launchState]);

  const submitPricingEmail = useCallback(async (email: string) => {
    const payload = buildGitHubScanPayload(email);

    if (!payload) {
      setLaunchState('error');
      setLaunchError('Select a GitHub repository and branch before starting the scan.');
      return;
    }

    const launchStartedAtMs = Date.now();

    setResultEmail(email);
    setLaunchState('submitting');
    setLaunchError('');

    try {
      const response = await requestFreeGitHubScan(payload);

      await waitForMinimumScanLaunchLoading(launchStartedAtMs);

      if (!response.runId) {
        throw new Error('Scan was accepted without a run ID.');
      }

      setScanResponse(response);
      setLaunchState('accepted');
      setIsResultEmailPromptOpen(false);
      viewRun(response.runId);
    } catch (launchErrorValue) {
      await waitForMinimumScanLaunchLoading(launchStartedAtMs);
      console.error('Failed to launch free scan:', launchErrorValue);
      void loadFreeScanAllowance();
      setLaunchState('error');
      setLaunchError(launchErrorValue instanceof Error && launchErrorValue.message
        ? launchErrorValue.message
        : 'Unable to start the scan. Please try again.');
    }
  }, [buildGitHubScanPayload, loadFreeScanAllowance, viewRun]);

  const acceptPaidScan = useCallback((response: ScanAcceptedResponse) => {
    if (!response.runId) {
      throw new Error(response.error || 'Payment completed, but the backend did not return a scan run.');
    }

    setScanResponse(response);
    setLaunchState('accepted');
    setIsPaymentOpen(false);
    viewRun(response.runId);
  }, [viewRun]);

  const getPaidScanPayload = useCallback((): Record<string, unknown> | null => {
    const payload = buildGitHubScanPayload(accountUser?.email);

    if (!payload || selectedPricingPlan === 'free') {
      return null;
    }

    return { ...payload, pricingPlan: selectedPricingPlan };
  }, [accountUser?.email, buildGitHubScanPayload, selectedPricingPlan]);

  const openPaidScanPayment = useCallback(async () => {
    const payload = getPaidScanPayload();

    if (!payload) {
      setLaunchState('error');
      setLaunchError('Select a GitHub repository and a paid plan before starting the scan.');
      return;
    }

    setLaunchState('submitting');
    setLaunchError('');

    try {
      // The backend checks for a retry credit first. Only ask for a card when a new payment is required.
      const response = await requestAdvancedScan(payload);

      if (response.runId) {
        acceptPaidScan(response);
        return;
      }

      if (response.paymentStatus === 'required') {
        setLaunchState('idle');
        setIsPaymentOpen(true);
        return;
      }

      throw new Error(response.error || 'The paid scan could not be started.');
    } catch (paymentError) {
      console.error('Failed to prepare paid scan:', paymentError);
      setLaunchState('error');
      setLaunchError(paymentError instanceof Error && paymentError.message
        ? paymentError.message
        : 'Unable to prepare payment. Please try again.');
    }
  }, [acceptPaidScan, getPaidScanPayload]);

  const submitPaidScanPayment = useCallback(async ({
    paymentMethodId,
    paymentAttemptId,
    stripe
  }: PaidScanPaymentSubmission) => {
    const payload = getPaidScanPayload();

    if (!payload) {
      setLaunchState('error');
      setLaunchError('The repository or paid plan selection is no longer available.');
      return;
    }

    setLaunchState('submitting');
    setLaunchError('');

    try {
      let response = await requestAdvancedScan({
        ...payload,
        payment: { paymentMethodId, paymentAttemptId }
      });

      if (response.paymentStatus === 'requires-action') {
        if (!response.clientSecret || !response.paymentIntentId) {
          throw new Error('Stripe authentication is required, but the backend returned an incomplete payment response.');
        }

        const confirmation = await stripe.confirmCardPayment(response.clientSecret);

        if (confirmation.error) {
          throw new Error(confirmation.error.message || 'Stripe authentication failed. No scan was started.');
        }

        if (confirmation.paymentIntent?.status !== 'succeeded') {
          throw new Error('Payment has not completed. No scan was started.');
        }

        response = await requestAdvancedScan({
          ...payload,
          payment: { paymentIntentId: response.paymentIntentId }
        });
      }

      if (!response.runId) {
        throw new Error(response.error || response.message || 'Payment failed. No scan was started.');
      }

      acceptPaidScan(response);
    } catch (paymentError) {
      console.error('Failed to launch paid scan:', paymentError);
      setLaunchState('error');
      setLaunchError(paymentError instanceof Error && paymentError.message
        ? paymentError.message
        : 'Payment failed. No scan was started. Please try again.');
    }
  }, [acceptPaidScan, getPaidScanPayload]);

  const installGitHubApp = useCallback(() => {
    const installUrl = githubConfig?.installUrl ?? selectedGithubInstallation?.htmlUrl;

    if (!installUrl) {
      setGithubSessionError('GitHub App install URL is not configured.');
      return;
    }

    window.location.href = installUrl;
  }, [githubConfig?.installUrl, selectedGithubInstallation?.htmlUrl]);

  const selectGithubRepository = useCallback((installationId: number, repositoryId: number) => {
    const repository = githubRepositoryChoices.find((choice) => {
      return choice.installation.id === installationId && choice.repository.id === repositoryId;
    })?.repository;

    setSelectedGithubInstallationId(installationId);
    setSelectedGithubRepositoryId(repositoryId);
    setSelectedGithubBranch(repository?.defaultBranch ?? '');
    setGithubBranches([]);
    setGithubBranchesState('loading');
    setGithubBranchesError('');
    setGithubTreeDirectories([]);
    setGithubTreeState('loading');
    setGithubTreeError('');
    setScopePaths([]);
  }, [githubRepositoryChoices]);

  const selectGithubBranch = useCallback((branch: string) => {
    setSelectedGithubBranch(branch);
    setScopePaths([]);
    setGithubTreeDirectories([]);
    setGithubTreeState('loading');
    setGithubTreeError('');
  }, []);

  const selectRepositoryRootScope = useCallback(() => {
    setScopePaths([]);
  }, []);

  const toggleScopePath = useCallback((scopePathToToggle: string) => {
    setScopePaths((currentScopePaths) => {
      if (currentScopePaths.includes(scopePathToToggle)) {
        return currentScopePaths.filter((path) => path !== scopePathToToggle);
      }

      const withoutCoveringAncestors = currentScopePaths.filter((path) => {
        return !scopePathToToggle.startsWith(`${path}/`);
      });

      return compactScopePaths([...withoutCoveringAncestors, scopePathToToggle]);
    });
  }, []);

  const status = details?.status;

  return (
    <div className={`app-shell run-shell${isSidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
      <div className="run-layout">
        <aside className="run-sidebar">
          <div className="run-sidebar-header">
            <a className="logo run-brand" href="/" title={isSidebarCollapsed ? 'Havu' : undefined}>
              <img className="logo-mark" src="/logoinvert.png" alt="" aria-hidden={true} />
              <span className="logo-text">Havu</span>
            </a>
            <button
              type="button"
              className="run-sidebar-toggle"
              aria-label={isSidebarCollapsed ? 'Expand side menu' : 'Minimize side menu'}
              aria-pressed={isSidebarCollapsed}
              onClick={() => setIsSidebarCollapsed((collapsed) => !collapsed)}
            >
              {isSidebarCollapsed ? <IconChevronRight aria-hidden={true} /> : <IconChevronLeft aria-hidden={true} />}
            </button>
          </div>
          <nav className="run-nav" aria-label="Run sections">
            {RUN_NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`run-nav-item${screen === item.id ? ' active' : ''}`}
                aria-current={screen === item.id ? 'page' : undefined}
                title={isSidebarCollapsed ? item.label : undefined}
                onClick={() => handleNavItemClick(item.id)}
              >
                <span className="run-nav-icon">
                  <RunNavIcon icon={item.icon} />
                </span>
                <span className="run-nav-label">{item.label}</span>
              </button>
            ))}
          </nav>
          <RunGitHubAccount
            isCollapsed={isSidebarCollapsed}
            isAuthenticated={isGitHubAuthenticated}
            login={githubSessionLogin}
            onOpenLoginPrompt={openGitHubLoginPrompt}
          />
        </aside>

        <div className="run-main-area">
          <header className="top-bar run-top-bar">
            <div className="run-top-main">
              <div className="run-header-actions">
                {isReady && runId && (
                  <details className="run-report-dropdown">
                    <summary className="secondary-link run-report-trigger" aria-label="Download report">
                      <IconDownload aria-hidden={true} />
                      <span>Report</span>
                      <IconChevronDown aria-hidden={true} />
                    </summary>
                    <div className="run-report-menu" role="menu" aria-label="Report formats">
                      <a href={runGetIssueMarkdownExportUrl(runId)} role="menuitem">
                        Markdown
                      </a>
                      <a href={runGetIssuePdfExportUrl(runId)} role="menuitem">
                        PDF
                      </a>
                    </div>
                  </details>
                )}
                {isGitHubAuthenticated ? (
                  <button
                    type="button"
                    className="primary-button run-rescan-link"
                    onClick={() => handleNavItemClick('pricing')}
                  >
                    {headerRunButtonLabel}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="primary-button run-rescan-link"
                    onClick={openGitHubLoginPrompt}
                  >
                    {headerRunButtonLabel}
                  </button>
                )}
              </div>
            </div>
          </header>

          <div className="run-content">
            {screen === 'findings' && (
              !runId ? (
                <main className="run-page-panel">
                  <div className="run-panel-heading">
                    <h1>Findings</h1>
                  </div>

                  <section className="run-settings-list" aria-label="No pentest yet">
                    <div className="run-settings-row">
                      <div>
                        <strong>No pentest yet</strong>
                        <span>Choose a repository and scope to run your first advanced pentest.</span>
                      </div>
                      <button type="button" className="primary-button run-panel-action" onClick={() => handleNavItemClick('scope')}>
                        Choose scope
                      </button>
                    </div>
                  </section>
                </main>
              ) : isReady ? (
                <RunIssueDashboard
                  issues={issues}
                  showBaselineCoverageNotice={Boolean(
                    status &&
                    !(status.scopePaths?.length) &&
                    !status.scopePath &&
                    !status.branch
                  )}
                  onShowDeepScanCoverage={() => setScreen('tools')}
                  onIssueAcceptanceChange={handleIssueAcceptanceChange}
                  runId={runId}
                  workspaceScans={workspaceScans}
                  workspaceScansState={workspaceScansState}
                  onSelectRun={viewRun}
                />
              ) : (
                <RunStatusPage
                  status={status}
                  loadState={loadState}
                  error={error}
                  isReady={isReady}
                  issueCount={issues.length}
                  nowMs={nowMs}
                  onRetry={() => void loadRun()}
                  onViewResults={() => setScreen('findings')}
                />
              )
            )}
          {screen === 'new-workspace' && (
            <RunNewWorkspaceScreen
              isAccountAuthenticated={isAccountAuthenticated}
              onRequireLogin={openGitHubLoginPrompt}
              workspacesState={workspacesState}
              workspaces={workspaces}
              currentWorkspaceId={currentWorkspaceId}
              onSelectWorkspace={selectWorkspace}
              newWorkspaceName={newWorkspaceName}
              onNewWorkspaceNameChange={setNewWorkspaceName}
              createWorkspaceState={createWorkspaceState}
              createWorkspaceError={createWorkspaceError}
              onCreateWorkspace={() => void createWorkspace()}
            />
          )}
          {screen === 'scope' && (
            <RunScopeScreen
              githubConfigState={githubConfigState}
              githubConfigError={githubConfigError}
              githubConfig={githubConfig}
              githubSessionState={githubSessionState}
              githubSessionError={githubSessionError}
              githubSessionId={githubSessionId}
              githubInstallations={githubInstallations}
              selectedGithubInstallationId={selectedGithubInstallationId}
              githubRepositoriesState={githubRepositoriesState}
              githubRepositoriesError={githubRepositoriesError}
              githubRepositoryChoices={githubRepositoryChoices}
              selectedGithubRepositoryId={selectedGithubRepositoryId}
              githubBranchesState={githubBranchesState}
              githubBranchesError={githubBranchesError}
              githubBranches={githubBranches}
              selectedGithubBranch={selectedGithubBranch}
              githubTreeState={githubTreeState}
              githubTreeError={githubTreeError}
              githubTreeDirectories={githubTreeDirectories}
              scopePaths={scopePaths}
              assumptionsFilePath={assumptionsFilePath}
              workspaceScopeSaveState={workspaceScopeSaveState}
              workspaceScopeSaveError={workspaceScopeSaveError}
              onConnectGitHub={connectGitHub}
              onInstallGitHubApp={installGitHubApp}
              onSelectGithubRepository={selectGithubRepository}
              onSelectGithubBranch={selectGithubBranch}
              onSelectRepositoryRootScope={selectRepositoryRootScope}
              onToggleScopePath={toggleScopePath}
              onAssumptionsFilePathChange={setAssumptionsFilePath}
              onReviewPricing={() => void reviewPricing()}
            />
          )}
          {screen === 'tools' && <RunToolsScreen />}
          {screen === 'pricing' && (
            <RunPricingScreen
              canRunScan={canRunGitHubScan}
              selectedRepositoryLabel={selectedRepositoryLabel}
              workspaceName={currentWorkspace?.name}
              pricingEstimateState={pricingEstimateState}
              pricingEstimateError={pricingEstimateError}
              pricingEstimate={pricingEstimate}
              launchState={launchState}
              launchError={launchError}
              scanResponse={scanResponse}
              freeScanAllowance={freeScanAllowance}
              isEmailPromptOpen={isResultEmailPromptOpen}
              resultEmail={resultEmail || accountUser?.email || ''}
              selectedPlan={selectedPricingPlan}
              isPaymentOpen={isPaymentOpen}
              onSelectPlan={(plan) => {
                setSelectedPricingPlan(plan);
                setLaunchError('');
                setLaunchState((state) => state === 'error' ? 'idle' : state);
              }}
              onOpenPayment={() => void openPaidScanPayment()}
              onClosePayment={() => {
                if (launchState !== 'submitting') {
                  setIsPaymentOpen(false);
                }
              }}
              onPaymentError={(message) => {
                setLaunchError(message);
                setLaunchState(message ? 'error' : 'idle');
              }}
              onSubmitPayment={submitPaidScanPayment}
              onOpenEmailPrompt={() => setIsResultEmailPromptOpen(true)}
              onCloseEmailPrompt={closeResultEmailPrompt}
              onSubmitEmail={submitPricingEmail}
              onViewRun={viewRun}
              onGoToScope={() => setScreen('scope')}
            />
          )}
          {screen === 'settings' && (
            <RunSettingsScreen
              status={status}
              runId={runId}
              githubSessionId={githubSessionId}
              githubConfig={githubConfig}
              githubInstallations={githubInstallations}
              selectedGithubInstallationId={selectedGithubInstallationId}
              isLogoutDisabled={logoutState === 'submitting'}
              logoutError={logoutError}
              onConnectGitHub={connectGitHub}
              onInstallGitHubApp={installGitHubApp}
              onLogout={() => void logout()}
            />
          )}
        </div>
        </div>
      </div>
      <RunGitHubLoginModal
        isOpen={isGitHubLoginPromptOpen}
        configState={githubConfigState}
        isConfigured={Boolean(githubConfig?.configured)}
        onCloseLoginPrompt={closeGitHubLoginPrompt}
        onLogin={connectGitHub}
      />
    </div>
  );
}

export default RunPage;

export type {
  RunIssueAcceptanceStatus,
  RunIssueViewModel,
  RunPageLoadState,
  RunStatusAgent,
  RunStatusDeduplication,
  RunStatusSnapshot
};
